import React, { useState, useRef, useEffect, useMemo } from "react";
import buildFileSystem from "./fileSystem";
import { executeCommand } from "./commands";

const WAITING_MESSAGES = [
  "One sec, thinking...",
  "Processing, hold on...",
  "Working on it...",
  "Give me a moment...",
  "Crunching the details...",
  "Still with you, almost there...",
];

const getRandomWaitingMessage = (excludeMessage = "") => {
  const candidates = WAITING_MESSAGES.filter((msg) => msg !== excludeMessage);
  if (candidates.length === 0) return WAITING_MESSAGES[0];
  return candidates[Math.floor(Math.random() * candidates.length)];
};

const InteractiveTerminal = ({ pendingCommand, onCommandConsumed }) => {
  const fileSystem = useMemo(() => buildFileSystem(), []);
  const initialHistory = useMemo(() => {
    const welcome = executeCommand("cat welcome.txt", [], fileSystem);
    return [
      { type: "input", text: "visitor@kevin:~$ cat welcome.txt" },
      ...welcome.output.map((text) => ({ type: "output", text })),
      { type: "output", text: "" },
      { type: "output", text: "Type 'help' for available commands." },
      { type: "output", text: "" },
    ];
  }, [fileSystem]);
  const [history, setHistory] = useState(initialHistory);
  const [currentPath, setCurrentPath] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [commandHistory, setCommandHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isStreaming, setIsStreaming] = useState(false);

  const outputRef = useRef(null);
  const inputRef = useRef(null);
  const waitingIntervalRef = useRef(null);

  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [history]);

  useEffect(() => {
    return () => {
      if (waitingIntervalRef.current) {
        clearInterval(waitingIntervalRef.current);
      }
    };
  }, []);

  // Execute commands triggered by clicking project/experience cards.
  useEffect(() => {
    if (!pendingCommand) return;
    const prompt = getPrompt();
    setHistory((prev) => [
      ...prev,
      { type: "input", text: prompt + pendingCommand },
    ]);
    const result = executeCommand(pendingCommand, currentPath, fileSystem);
    if (result.newPath !== undefined) setCurrentPath(result.newPath);
    if (result.output.length > 0) {
      setHistory((prev) => [
        ...prev,
        ...result.output.map((text) => ({ type: "output", text })),
      ]);
    }
    onCommandConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingCommand]);

  const getPrompt = () => {
    const path = "~" + (currentPath.length > 0 ? "/" + currentPath.join("/") : "");
    return `visitor@kevin:${path}$ `;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const input = inputValue.trim();
    const prompt = getPrompt();

    // Add the input line to history
    setHistory((prev) => [...prev, { type: "input", text: prompt + input }]);
    setInputValue("");

    if (input) {
      setCommandHistory((prev) => [...prev, input]);
      setHistoryIndex(-1);
    }

    if (!input) return;

    const result = executeCommand(input, currentPath, fileSystem);

    if (result.clear) {
      setHistory([]);
      return;
    }

    if (result.newPath !== undefined) {
      setCurrentPath(result.newPath);
    }

    if (result.output.length > 0) {
      setHistory((prev) => [
        ...prev,
        ...result.output.map((text) => ({ type: "output", text })),
      ]);
    }

    // Handle streaming AI responses
    if (result.stream) {
      setIsStreaming(true);
      const initialWaitingMessage = getRandomWaitingMessage();

      if (waitingIntervalRef.current) {
        clearInterval(waitingIntervalRef.current);
      }

      // Add a streaming placeholder line
      setHistory((prev) => [
        ...prev,
        { type: "stream", text: initialWaitingMessage, isStatus: true },
      ]);

      waitingIntervalRef.current = setInterval(() => {
        setHistory((prev) => {
          const newHistory = [...prev];
          const streamIdx = newHistory.findLastIndex((e) => e.type === "stream");
          if (streamIdx === -1) return prev;

          const streamEntry = newHistory[streamIdx];
          if (!streamEntry.isStatus) return prev;

          newHistory[streamIdx] = {
            ...streamEntry,
            text: getRandomWaitingMessage(streamEntry.text),
          };
          return newHistory;
        });
      }, 1300);

      result.stream(
        // onUpdate — called with each new chunk
        (partialText) => {
          if (waitingIntervalRef.current) {
            clearInterval(waitingIntervalRef.current);
            waitingIntervalRef.current = null;
          }
          setHistory((prev) => {
            const newHistory = [...prev];
            const streamIdx = newHistory.findLastIndex((e) => e.type === "stream");
            if (streamIdx !== -1) {
              newHistory[streamIdx] = {
                type: "stream",
                text: partialText,
                isStatus: false,
              };
            }
            return [...newHistory];
          });
        },
        // onDone — finalize the streamed line
        (finalText) => {
          if (waitingIntervalRef.current) {
            clearInterval(waitingIntervalRef.current);
            waitingIntervalRef.current = null;
          }
          setHistory((prev) => {
            const newHistory = [...prev];
            const streamIdx = newHistory.findLastIndex((e) => e.type === "stream");
            if (streamIdx !== -1) {
              newHistory[streamIdx] = { type: "output", text: finalText };
            }
            return [...newHistory];
          });
          setIsStreaming(false);
        }
      );
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (commandHistory.length === 0) return;
      const newIndex =
        historyIndex === -1
          ? commandHistory.length - 1
          : Math.max(0, historyIndex - 1);
      setHistoryIndex(newIndex);
      setInputValue(commandHistory[newIndex]);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (historyIndex === -1) return;
      const newIndex = historyIndex + 1;
      if (newIndex >= commandHistory.length) {
        setHistoryIndex(-1);
        setInputValue("");
      } else {
        setHistoryIndex(newIndex);
        setInputValue(commandHistory[newIndex]);
      }
    }
  };

  const focusInput = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <div className="h-full w-full flex flex-col">
      <div
        className="w-full rounded-lg overflow-hidden flex-1 flex flex-col shadow-lg"
        style={{
          background: "var(--terminal-bg)",
          border: "1px solid var(--border-color)",
        }}
      >
        {/* Terminal Header */}
        <div
          className="flex items-center px-3 py-2 border-b"
          style={{
            background: "rgba(0, 0, 0, 0.3)",
            borderColor: "rgba(255, 255, 255, 0.1)",
          }}
        >
          <div className="flex gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F56]"></div>
            <div className="w-2.5 h-2.5 rounded-full bg-[#FFBD2E]"></div>
            <div className="w-2.5 h-2.5 rounded-full bg-[#27C93F]"></div>
          </div>
          <div
            className="flex-1 text-center text-[10px] font-medium font-mono"
            style={{ color: "var(--text-secondary)" }}
          >
            kevin@portfolio
          </div>
        </div>

        {/* Terminal Body */}
        <div
          ref={outputRef}
          className="p-3 font-mono text-[11px] flex-1 overflow-y-auto cursor-text"
          style={{ color: "var(--terminal-text)" }}
          onClick={focusInput}
        >
          {history.map((entry, i) => (
            <div key={i} className="whitespace-pre-wrap leading-relaxed">
              {entry.type === "input" ? (
                <span>
                  <span style={{ color: "var(--accent)" }}>
                    {entry.text.split("$ ")[0]}${" "}
                  </span>
                  <span style={{ color: "var(--text-primary)" }}>
                    {entry.text.split("$ ").slice(1).join("$ ")}
                  </span>
                </span>
              ) : entry.type === "stream" ? (
                <span style={{ color: "var(--text-secondary)" }}>
                  {entry.text}
                  <span className="animate-pulse">_</span>
                </span>
              ) : (
                <span style={{ color: "var(--text-secondary)" }}>
                  {entry.text}
                </span>
              )}
            </div>
          ))}

          {/* Input Line */}
          <form onSubmit={handleSubmit} className="flex items-center">
            <span style={{ color: "var(--accent)" }}>{getPrompt()}</span>
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              className="flex-1 bg-transparent border-none outline-none font-mono text-[11px]"
              style={{
                color: "var(--text-primary)",
                caretColor: "var(--accent)",
              }}
              autoComplete="off"
              spellCheck="false"
              autoFocus
              disabled={isStreaming}
            />
          </form>
        </div>
      </div>
    </div>
  );
};

export default InteractiveTerminal;
